import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, Save, Info, AlertTriangle } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useToast } from "../../hooks/use-toast";

/**
 * Modal for managing which tasks are attached to a project.
 * Props:
 *   project: { id, name }
 *   onClose: fn
 */
export default function ProjectTasksModal({ project, onClose }) {
  const { toast } = useToast();
  const [links, setLinks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addSel, setAddSel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [l, t] = await Promise.all([api.projectTasks(project.id), api.tasks(false)]);
      setLinks(l);
      setTasks(t);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project?.id]);

  const attachedIds = new Set(links.map((l) => l.task_id));
  const available = tasks.filter((t) => !attachedIds.has(t.id));

  const attach = async () => {
    if (!addSel) return;
    try {
      await api.attachProjectTask(project.id, { task_id: addSel });
      setAddSel("");
      await load();
    } catch (ex) {
      toast({ title: "Couldn't attach task", description: apiErrorMessage(ex, "") });
    }
  };

  const detach = async (link) => {
    if (!window.confirm(`Remove "${link.task_name}" from this project? Existing time entries stay put.`)) return;
    try {
      await api.detachProjectTask(project.id, link.id);
      await load();
    } catch (ex) {
      toast({ title: "Couldn't remove task", description: apiErrorMessage(ex, "") });
    }
  };

  const patchLink = async (link, patch) => {
    try {
      await api.patchProjectTask(project.id, link.id, patch);
      await load();
    } catch (ex) {
      toast({ title: "Couldn't update task", description: apiErrorMessage(ex, "") });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto"
           data-testid="project-tasks-modal">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl">Tasks on {project.name}</h2>
            <p className="text-xs text-[#6a736f] mt-1">
              Pick which tasks your team can log time against on this project. Override the unit cost per project.
            </p>
          </div>
          <button onClick={onClose} className="text-[#6a736f]"><X size={18} /></button>
        </div>

        {/* Attach a new task */}
        <div className="border border-cream rounded-lg p-3 bg-cream-2/40">
          <div className="text-xs font-semibold text-[#3f4a44] mb-2">Attach a task</div>
          {available.length === 0 ? (
            <div className="text-xs text-[#6a736f] inline-flex items-center gap-1">
              <Info size={12} /> All tasks in your library are already attached. Add more from the <a href="/app/tasks" className="text-coral underline">Task library</a>.
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <select
                value={addSel} onChange={(e) => setAddSel(e.target.value)}
                data-testid="attach-task-select"
                className="flex-1 border border-cream rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">Select a task…</option>
                {available.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.default_cost > 0 ? ` — $${t.default_cost}/hr baseline` : ""}
                    {t.default_billable ? "" : " · non-billable"}
                  </option>
                ))}
              </select>
              <button
                onClick={attach} disabled={!addSel}
                data-testid="attach-task-btn"
                className="btn-coral inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium disabled:opacity-40"
              >
                <Plus size={14} /> Attach
              </button>
            </div>
          )}
        </div>

        {/* Attached tasks list */}
        <div className="border border-cream rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
              <tr className="text-left">
                <th className="px-3 py-2">Task</th>
                <th className="px-3 py-2 text-right">Cost / hour</th>
                <th className="px-3 py-2 text-center">Billable</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAF3EE]">
              {loading ? (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-[#6a736f]">Loading…</td></tr>
              ) : links.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-[#6a736f]" data-testid="project-tasks-empty">
                  <AlertTriangle size={14} className="inline text-coral mr-1" />
                  No tasks yet — attach one above so people can log time.
                </td></tr>
              ) : links.map((l) => (
                <LinkRow key={l.id} link={l} onDetach={() => detach(l)} onPatch={(p) => patchLink(l, p)} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <button onClick={onClose} className="btn-outline-forest px-4 py-2 rounded-md text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}

function LinkRow({ link, onDetach, onPatch }) {
  const overrideActive = link.cost_override !== null && link.cost_override !== undefined;
  const [cost, setCost] = useState(overrideActive ? link.cost_override : link.default_cost);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setCost(overrideActive ? link.cost_override : link.default_cost);
  }, [link.id, overrideActive, link.cost_override, link.default_cost]);

  const saveCost = async () => {
    setEditing(false);
    const value = Number(cost);
    if (Number.isNaN(value)) return;
    // If user set it back to the baseline default, clear the override
    if (value === link.default_cost) {
      await onPatch({ clear_cost_override: true });
    } else {
      await onPatch({ cost_override: value });
    }
  };

  const toggleBillable = async () => {
    // Cycle: baseline → override-true → override-false → baseline
    if (link.billable_override === null || link.billable_override === undefined) {
      await onPatch({ billable_override: !link.default_billable });
    } else if (link.billable_override !== link.default_billable) {
      await onPatch({ clear_billable_override: true });
    } else {
      await onPatch({ clear_billable_override: true });
    }
  };

  return (
    <tr data-testid={`project-task-row-${link.id}`}>
      <td className="px-3 py-2">
        <div className="font-medium">{link.task_name}</div>
        <div className="text-[10px] text-[#6a736f]">
          Baseline ${link.default_cost.toFixed(2)}/hr · {link.default_billable ? "billable" : "non-billable"}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        {editing ? (
          <div className="inline-flex items-center gap-1">
            <input
              type="number" min={0} step="0.01" value={cost}
              onChange={(e) => setCost(e.target.value)}
              data-testid={`cost-input-${link.id}`}
              className="w-20 border border-cream rounded-md px-2 py-1 text-sm text-right"
            />
            <button onClick={saveCost} data-testid={`save-cost-${link.id}`} className="text-forest"><Save size={14} /></button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            data-testid={`edit-cost-${link.id}`}
            className="text-sm tabular-nums text-forest hover:text-coral"
          >
            ${link.effective_cost.toFixed(2)}
            {overrideActive && <span className="ml-1 text-[10px] bg-coral/15 text-coral px-1 rounded">override</span>}
          </button>
        )}
      </td>
      <td className="px-3 py-2 text-center">
        <button
          onClick={toggleBillable}
          data-testid={`toggle-billable-${link.id}`}
          className={`text-xs px-2 py-1 rounded ${link.effective_billable ? "bg-emerald-100 text-emerald-800" : "bg-cream text-[#3f4a44]"}`}
        >
          {link.effective_billable ? "Billable" : "Non-billable"}
          {(link.billable_override !== null && link.billable_override !== undefined) &&
            <span className="ml-1 text-[10px] text-coral">override</span>}
        </button>
      </td>
      <td className="px-3 py-2 text-right">
        <button onClick={onDetach}
                data-testid={`detach-task-${link.id}`}
                className="text-[#6a736f] hover:text-red-600 inline-flex items-center gap-1 text-xs">
          <Trash2 size={12} /> Remove
        </button>
      </td>
    </tr>
  );
}
